import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import { Platform } from 'react-native';

async function toDataUrl(url) {
  if (!url) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const buffer = await res.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    const ext = url.split('?')[0].split('.').pop().toLowerCase();
    const mime = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp' }[ext] || 'image/jpeg';
    let binary = '';
    for (let i = 0; i < bytes.length; i += 8192) {
      binary += String.fromCharCode(...bytes.slice(i, i + 8192));
    }
    return `data:${mime};base64,${btoa(binary)}`;
  } catch {
    return null;
  }
}

function escapeHtml(str) {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}

function printOnWeb(html, filename) {
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); }, 500);
}

function formatCurrency(n) {
  if (!n && n !== 0) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2,
  }).format(n);
}

function formatDate(ds) {
  if (!ds) return '';
  return new Date(ds).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

const sharedStyles = `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    font-family: -apple-system, 'Helvetica Neue', Arial, sans-serif;
    color: #111827;
    font-size: 13px;
    background: #fff;
    padding: 48px 52px;
    line-height: 1.5;
  }

  /* ── Header ── */
  .header {
    display: flex;
    justify-content: space-between;
    align-items: stretch;
    background: #0A0F1E;
    border-radius: 12px;
    padding: 28px 32px;
    margin-bottom: 36px;
  }
  .header-left { display: flex; align-items: center; gap: 16px; }
  .logo-img {
    width: 52px; height: 52px;
    border-radius: 10px;
    object-fit: cover;
    border: 1px solid rgba(255,255,255,0.12);
    flex-shrink: 0;
  }
  .brand { font-size: 22px; font-weight: 700; letter-spacing: -0.5px; color: #fff; }
  .brand-trade { font-size: 11px; color: rgba(255,255,255,0.4); margin-top: 3px; letter-spacing: 0.3px; }
  .header-right { text-align: right; display: flex; flex-direction: column; justify-content: center; gap: 8px; }
  .doc-type {
    font-size: 11px; font-weight: 700;
    letter-spacing: 2px; text-transform: uppercase;
    color: rgba(255,255,255,0.35);
  }
  .doc-date { font-size: 12px; color: rgba(255,255,255,0.65); }

  /* ── Status badge ── */
  .badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 99px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  .badge-draft    { background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.5); }
  .badge-sent     { background: rgba(99,179,237,0.18); color: #63b3ed; }
  .badge-accepted { background: rgba(52,211,153,0.18); color: #34d399; }
  .badge-declined { background: rgba(252,129,129,0.18); color: #fc8181; }
  .badge-unpaid   { background: rgba(251,191,36,0.18); color: #fbbf24; }
  .badge-paid     { background: rgba(52,211,153,0.18); color: #34d399; }
  .badge-overdue  { background: rgba(252,129,129,0.18); color: #fc8181; }

  /* ── Two-column meta row ── */
  .meta-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 32px;
    gap: 24px;
  }
  .meta-block { flex: 1; }
  .meta-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    color: #9ca3af;
    margin-bottom: 6px;
  }
  .meta-name { font-size: 19px; font-weight: 700; color: #111827; margin-bottom: 3px; }
  .meta-detail { font-size: 12px; color: #6b7280; margin-top: 1px; }

  /* ── Due date highlight ── */
  .due-banner {
    background: #eff6ff;
    border-radius: 8px;
    padding: 11px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 28px;
  }
  .due-banner-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #3b82f6; }
  .due-banner-date  { font-size: 14px; font-weight: 700; color: #1d4ed8; }

  /* ── Section ── */
  .section { margin-bottom: 28px; }
  .section-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1.4px;
    color: #9ca3af;
    margin-bottom: 10px;
  }

  /* ── Scope box ── */
  .scope-box {
    background: #f9fafb;
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    padding: 14px 16px;
    font-size: 13px;
    color: #374151;
    line-height: 1.65;
  }
  .scope-duration {
    font-size: 11px;
    color: #9ca3af;
    margin-top: 8px;
  }

  /* ── Line items table ── */
  table { width: 100%; border-collapse: collapse; }
  thead tr { background: #f9fafb; }
  th {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: #9ca3af;
    padding: 9px 12px;
    border-top: 1px solid #f0f0f0;
    border-bottom: 1px solid #f0f0f0;
    text-align: left;
  }
  td {
    padding: 11px 12px;
    border-bottom: 1px solid #f5f5f5;
    font-size: 12.5px;
    color: #374151;
    vertical-align: top;
  }
  tr:last-child td { border-bottom: none; }
  .td-desc { color: #111827; font-weight: 500; }
  .td-muted { color: #6b7280; }

  /* ── Totals ── */
  .totals-wrap { max-width: 260px; margin-left: auto; }
  .totals-row {
    display: flex;
    justify-content: space-between;
    padding: 6px 0;
    font-size: 12.5px;
    color: #6b7280;
  }
  .totals-divider { border: none; border-top: 1px solid #e5e7eb; margin: 8px 0; }
  .totals-final {
    display: flex;
    justify-content: space-between;
    padding: 10px 0 4px;
    font-size: 17px;
    font-weight: 800;
    color: #111827;
  }
  .totals-final-amount { color: #059669; }

  /* ── Notes ── */
  .notes-box {
    background: #fffbeb;
    border-left: 3px solid #f59e0b;
    border-radius: 0 8px 8px 0;
    padding: 12px 16px;
    font-size: 12.5px;
    color: #44403c;
    line-height: 1.6;
  }

  /* ── Footer ── */
  .footer {
    margin-top: 52px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    color: #d1d5db;
    letter-spacing: 0.3px;
  }
`;

export async function exportQuotePDF(quote, businessName = 'Slate Quotes', logoUrl = null, showItemized = true, isPro = true) {
  const lineItems = Array.isArray(quote.line_items) ? quote.line_items : [];
  const markupMultiplier = 1 + ((quote.markup_percent || 0) / 100);
  const logoDataUrl = await toDataUrl(logoUrl);
  const status = (quote.status || 'draft').toLowerCase();

  const itemRows = lineItems.map((item, i) => `
    <tr style="${i % 2 === 0 ? '' : 'background:#fafafa;'}">
      <td class="td-desc">${escapeHtml(item.description)}</td>
      <td class="td-muted" style="text-align:center;white-space:nowrap;">${escapeHtml(item.quantity)} ${escapeHtml(item.unit)}</td>
      <td class="td-muted" style="text-align:right;white-space:nowrap;">${formatCurrency((item.unitPrice || 0) * markupMultiplier)}</td>
      <td style="text-align:right;font-weight:600;color:#111827;white-space:nowrap;">${formatCurrency((item.total || 0) * markupMultiplier)}</td>
    </tr>
  `).join('');

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>${sharedStyles}</style>
</head>
<body>

<div class="header">
  <div class="header-left">
    ${logoDataUrl ? `<img src="${logoDataUrl}" class="logo-img" />` : ''}
    <div>
      <div class="brand">${escapeHtml(businessName)}</div>
      ${quote.trade_type ? `<div class="brand-trade">${escapeHtml(quote.trade_type)}</div>` : ''}
    </div>
  </div>
  <div class="header-right">
    <div class="doc-type">Quote</div>
    <div class="doc-date">${formatDate(quote.created_at)}</div>
    <div><span class="badge badge-${status}">${status}</span></div>
  </div>
</div>

<div class="meta-row">
  <div class="meta-block">
    <div class="meta-label">Prepared For</div>
    <div class="meta-name">${escapeHtml(quote.client_name)}</div>
    ${quote.client_email ? `<div class="meta-detail">${escapeHtml(quote.client_email)}</div>` : ''}
    ${quote.client_phone ? `<div class="meta-detail">${escapeHtml(quote.client_phone)}</div>` : ''}
  </div>
  ${quote.estimated_days ? `
  <div class="meta-block" style="text-align:right;">
    <div class="meta-label">Est. Duration</div>
    <div style="font-size:16px;font-weight:700;color:#111827;">${escapeHtml(quote.estimated_days)} day${quote.estimated_days !== 1 ? 's' : ''}</div>
  </div>` : ''}
</div>

${quote.scope_of_work ? `
<div class="section">
  <div class="section-label">Scope of Work</div>
  <div class="scope-box">${escapeHtml(quote.scope_of_work)}</div>
</div>` : ''}

${showItemized && itemRows ? `
<div class="section">
  <div class="section-label">Line Items</div>
  <table>
    <thead>
      <tr>
        <th>Description</th>
        <th style="text-align:center">Qty / Unit</th>
        <th style="text-align:right">Unit Price</th>
        <th style="text-align:right">Total</th>
      </tr>
    </thead>
    <tbody>${itemRows}</tbody>
  </table>
</div>` : ''}

<div class="section">
  <div class="totals-wrap">
    <div class="totals-row"><span>Subtotal</span><span>${formatCurrency((quote.subtotal || 0) * markupMultiplier)}</span></div>
    ${quote.tax_amount ? `<div class="totals-row"><span>Tax (${quote.tax_rate || 0}%)</span><span>${formatCurrency(quote.tax_amount)}</span></div>` : ''}
    <hr class="totals-divider" />
    <div class="totals-final">
      <span>Total</span>
      <span class="totals-final-amount">${formatCurrency(quote.total)}</span>
    </div>
  </div>
</div>

${quote.notes ? `
<div class="section">
  <div class="section-label">Notes</div>
  <div class="notes-box">${escapeHtml(quote.notes)}</div>
</div>` : ''}

<div class="footer">
  <span>${isPro ? 'Generated with Slate · Contractor Quoting' : '⚡ Created with <strong>Slate Quotes</strong> — Try it free'}</span>
  <span>${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
</div>

</body>
</html>`;

  if (Platform.OS === 'web') {
    printOnWeb(html, `Quote-${quote.client_name}`);
    return;
  }

  const { uri } = await Print.printToFileAsync({ html, base64: false });
  const canShare = await Sharing.isAvailableAsync();
  if (canShare) {
    await Sharing.shareAsync(uri, {
      mimeType: 'application/pdf',
      dialogTitle: `Quote — ${quote.client_name}`,
      UTI: 'com.adobe.pdf',
    });
  } else {
    await Print.printAsync({ uri });
  }
}

export async function exportInvoicePDF(invoice, businessName = 'Slate Quotes', logoUrl = null, isPro = true) {
  const lineItems = Array.isArray(invoice.line_items) ? invoice.line_items : [];
  const logoDataUrl = await toDataUrl(logoUrl);
  const status = (invoice.status || 'unpaid').toLowerCase();

  const itemRows = lineItems.map((item, i) => `
    <tr style="${i % 2 === 0 ? '' : 'background:#fafafa;'}">
      <td class="td-desc">${escapeHtml(item.description)}</td>
      <td class="td-muted" style="text-align:center;white-space:nowrap;">${escapeHtml(item.quantity)} ${escapeHtml(item.unit)}</td>
      <td class="td-muted" style="text-align:right;white-space:nowrap;">${formatCurrency(item.unitPrice)}</td>
      <td style="text-align:right;font-weight:600;color:#111827;white-space:nowrap;">${formatCurrency(item.total)}</td>
    </tr>
  `).join('');

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>${sharedStyles}</style>
</head>
<body>

<div class="header">
  <div class="header-left">
    ${logoDataUrl ? `<img src="${logoDataUrl}" class="logo-img" />` : ''}
    <div>
      <div class="brand">${escapeHtml(businessName)}</div>
    </div>
  </div>
  <div class="header-right">
    <div class="doc-type">Invoice</div>
    <div class="doc-date">${formatDate(invoice.created_at)}</div>
    <div><span class="badge badge-${status}">${status}</span></div>
  </div>
</div>

${invoice.due_date ? `
<div class="due-banner">
  <span class="due-banner-label">Payment Due</span>
  <span class="due-banner-date">${formatDate(invoice.due_date)}</span>
</div>` : ''}

<div class="meta-row">
  <div class="meta-block">
    <div class="meta-label">Bill To</div>
    <div class="meta-name">${escapeHtml(invoice.client_name)}</div>
    ${invoice.client_email ? `<div class="meta-detail">${escapeHtml(invoice.client_email)}</div>` : ''}
    ${invoice.client_phone ? `<div class="meta-detail">${escapeHtml(invoice.client_phone)}</div>` : ''}
  </div>
</div>

<div class="section">
  <div class="section-label">Line Items</div>
  <table>
    <thead>
      <tr>
        <th>Description</th>
        <th style="text-align:center">Qty / Unit</th>
        <th style="text-align:right">Unit Price</th>
        <th style="text-align:right">Total</th>
      </tr>
    </thead>
    <tbody>${itemRows}</tbody>
  </table>
</div>

<div class="section">
  <div class="totals-wrap">
    <div class="totals-row"><span>Subtotal</span><span>${formatCurrency(invoice.subtotal)}</span></div>
    ${invoice.tax_amount ? `<div class="totals-row"><span>Tax (${invoice.tax_rate || 0}%)</span><span>${formatCurrency(invoice.tax_amount)}</span></div>` : ''}
    <hr class="totals-divider" />
    <div class="totals-final">
      <span>Total Due</span>
      <span class="totals-final-amount">${formatCurrency(invoice.total)}</span>
    </div>
  </div>
</div>

${invoice.notes ? `
<div class="section">
  <div class="section-label">Notes</div>
  <div class="notes-box">${escapeHtml(invoice.notes)}</div>
</div>` : ''}

<div class="footer">
  <span>${isPro ? 'Generated with Slate · Contractor Quoting' : '⚡ Created with <strong>Slate Quotes</strong> — Try it free'}</span>
  <span>${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
</div>

</body>
</html>`;

  if (Platform.OS === 'web') {
    printOnWeb(html, `Invoice-${invoice.client_name}`);
    return;
  }

  const { uri } = await Print.printToFileAsync({ html, base64: false });
  const canShare = await Sharing.isAvailableAsync();
  if (canShare) {
    await Sharing.shareAsync(uri, {
      mimeType: 'application/pdf',
      dialogTitle: `Invoice — ${invoice.client_name}`,
      UTI: 'com.adobe.pdf',
    });
  } else {
    await Print.printAsync({ uri });
  }
}
